import {isMapPolygonFillVisible} from '@components/openstreetmap/constants/map-polygon-fill-visibility.state';

/**
 * Draws one geojson-vt polygon onto a canvas tile context (polygon type === 3 in vt).
 */
export function paintVtPolygonOnCanvasTile(
  ctx: CanvasRenderingContext2D,
  feature: any,
  scale: number,
  thinStrokeForLargeDataset: boolean,
  fallbackStroke: string,
  fallbackFill: string,
  /** Live slider opacity (0–1); when set, overrides feature tag fillOpacity without rebuilding VT. */
  fillOpacityOverride?: number | null,
): void {
  const tags = feature.tags || {};
  ctx.strokeStyle = tags.strokeColor ?? fallbackStroke;
  ctx.fillStyle = tags.fillColor ?? fallbackFill;
  ctx.lineWidth = thinStrokeForLargeDataset
    ? Math.max(0.5, 0.9)
    : Math.max(0.75, tags.strokeWidth ?? 2);
  const dash = tags.strokeDashArray;
  if (Array.isArray(dash) && dash.length) {
    ctx.setLineDash(dash);
  } else {
    ctx.setLineDash([]);
  }

  const geometry = feature.geometry;
  if (feature.type !== 3 || !geometry?.length) return;

  ctx.beginPath();
  for (let ri = 0; ri < geometry.length; ri++) {
    const ring = geometry[ri];
    if (!ring?.length) continue;
    for (let j = 0; j < ring.length; j++) {
      const p = ring[j];
      const x = p[0] * scale;
      const y = p[1] * scale;
      if (j === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.closePath();
  }
  const fillOpacity = fillOpacityOverride != null
    ? fillOpacityOverride
    : (typeof tags.fillOpacity === 'number' ? tags.fillOpacity : 0.8);
  if (isMapPolygonFillVisible()) {
    ctx.globalAlpha = fillOpacity;
    ctx.fill();
    ctx.globalAlpha = 1;
  }
  ctx.stroke();
}

/**
 * Repaint an already-mounted Leaflet GridLayer of canvas tiles without removing them
 * (avoids the disappear/reappear flash of {@link GridLayer#redraw}).
 */
export function repaintLeafletGridLayerCanvases(
  layer: any,
  paint: (ctx: CanvasRenderingContext2D, coords: {x: number; y: number; z: number}) => void,
): void {
  const tiles = layer?._tiles;
  if (!tiles) {
    return;
  }
  for (const key of Object.keys(tiles)) {
    const rec = tiles[key];
    const el = rec?.el as HTMLElement | undefined;
    const canvas = el instanceof HTMLCanvasElement
      ? el
      : el?.querySelector?.('canvas') ?? undefined;
    const ctx = canvas?.getContext?.('2d');
    const coords = rec?.coords;
    if (!ctx || !canvas || coords == null) {
      continue;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    paint(ctx, {x: coords.x, y: coords.y, z: coords.z});
  }
}
